import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './Admin.css';

const fmtRs = (v) => {
  if (v === null || v === undefined || isNaN(v)) return '—';
  const n = Number(v);
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const PnL = ({ value, pct }) => {
  if (value === null || value === undefined) return <span>—</span>;
  const cls = value > 0 ? 'pnl-pos' : value < 0 ? 'pnl-neg' : 'pnl-flat';
  return (
    <span className={cls}>
      {value > 0 ? '+' : ''}{fmtRs(value)}
      {pct !== undefined && pct !== null && ` (${pct > 0 ? '+' : ''}${pct}%)`}
    </span>
  );
};

function Admin({ onClose }) {
  const [activeTab, setActiveTab] = useState('trading');

  // ── User management state ──
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState({ user_id: '', name: '', email: '', role: 'user' });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [createdPassword, setCreatedPassword] = useState('');
  const [emailStatus, setEmailStatus] = useState('');

  // ── Client trading state ──
  const [overview, setOverview] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [closingPos, setClosingPos] = useState(null);
  const [closeMsg, setCloseMsg] = useState('');

  // ── Per-segment trading halt (admin STOP/RESUME for ALL users) ──
  const [segHalt, setSegHalt] = useState(null);

  // ── System health / activity-log panel ──
  const [health, setHealth] = useState(null);

  // ── AI recommendations state ──
  const [recs, setRecs] = useState([]);
  const [expandedRec, setExpandedRec] = useState(null);

  // ── Real performance state ──
  const [perf, setPerf] = useState(null);
  const [mlStatus, setMlStatus] = useState(null);   // AI maturity level

  useEffect(() => {
    loadUsers();
  }, []);

  const loadOverview = useCallback(async () => {
    try {
      const result = await api.adminTradingOverview();
      if (result.status === 'success') setOverview(result);
    } catch (err) { /* keep last good data */ }
  }, []);

  const loadHealth = useCallback(async () => {
    try {
      const r = await api.getHealth();
      if (r.status === 'success') setHealth(r);
    } catch (err) { /* keep last good data */ }
  }, []);

  const loadSegHalt = useCallback(async () => {
    try {
      const r = await api.getSegmentHalt();
      if (r.status === 'success') setSegHalt(r);
    } catch (err) { /* keep last good data */ }
  }, []);

  const toggleSeg = async (seg, halt) => {
    try {
      const r = await api.setSegmentHalt(seg, halt);
      if (r.status === 'success') { setMessage(`${seg.replace('_', ' ')} ${halt ? 'STOPPED' : 'RESUMED'} for all users`); loadSegHalt(); }
      else setError(r.message || 'Failed');
    } catch (err) { setError('Failed to update ' + seg); }
  };

  const loadClientDetail = useCallback(async (userId) => {
    try {
      const result = await api.adminClientTrading(userId);
      if (result.status === 'success') setClientDetail(result);
    } catch (err) { /* keep last good data */ }
  }, []);

  const loadRecs = useCallback(async () => {
    try {
      const result = await api.adminRecommendations();
      if (result.status === 'success') setRecs(result.recommendations);
    } catch (err) { /* keep last good data */ }
  }, []);

  const loadPerf = useCallback(async () => {
    try {
      const result = await api.adminPerformance();
      if (result.status === 'ok') setPerf(result);
    } catch (err) { /* keep last good data */ }
  }, []);

  // Auto-refresh the active tab every 5s
  useEffect(() => {
    if (activeTab === 'trading') {
      loadOverview();
      loadSegHalt();
      if (selectedClient) loadClientDetail(selectedClient);
      const t = setInterval(() => {
        loadOverview();
        if (selectedClient) loadClientDetail(selectedClient);
      }, 5000);
      return () => clearInterval(t);
    }
    if (activeTab === 'ai') {
      loadRecs();
      const loadMl = () => api.getMlStatus().then(setMlStatus).catch(() => {});
      loadMl();
      const t = setInterval(() => { loadRecs(); loadMl(); }, 5000);
      return () => clearInterval(t);
    }
    if (activeTab === 'performance') {
      loadPerf();
      const loadMl = () => api.getMlStatus().then(setMlStatus).catch(() => {});
      loadMl();
      const t = setInterval(() => { loadPerf(); loadMl(); }, 15000);
      return () => clearInterval(t);
    }
    if (activeTab === 'health') {
      loadHealth();
      const t = setInterval(loadHealth, 4000);
      return () => clearInterval(t);
    }
  }, [activeTab, selectedClient, loadOverview, loadClientDetail, loadRecs, loadPerf, loadSegHalt, loadHealth]);

  const loadUsers = async () => {
    try {
      const result = await api.adminListUsers();
      if (result.status === 'success') {
        setUsers(result.users);
      }
    } catch (err) {
      setError('Failed to load users');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newUser.user_id || !newUser.name || !newUser.email) {
      setError('User ID, name, and email are required');
      return;
    }
    if (!newUser.email.includes('@')) {
      setError('Valid email is required');
      return;
    }
    setLoading(true);
    setError('');
    setMessage('');
    setCreatedPassword('');
    setEmailStatus('');
    try {
      const result = await api.adminCreateUser(newUser);
      if (result.status === 'success') {
        setCreatedPassword(result.password);
        setEmailStatus(result.email_status || 'unknown');
        setMessage(`User "${newUser.user_id}" created. Password sent to ${newUser.email}`);
        setNewUser({ user_id: '', name: '', email: '', role: 'user' });
        loadUsers();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Failed to create user');
    }
    setLoading(false);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm(`Delete user "${userId}"?`)) return;
    try {
      const result = await api.adminDeleteUser(userId);
      if (result.status === 'success') {
        setMessage(`User "${userId}" deleted`);
        loadUsers();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Failed to delete user');
    }
  };

  const handleToggle = async (userId) => {
    try {
      const result = await api.adminToggleUser(userId);
      if (result.status === 'success') {
        setMessage(`User "${userId}" ${result.active ? 'activated' : 'deactivated'}`);
        loadUsers();
      }
    } catch (err) {
      setError('Failed to toggle user');
    }
  };

  const copyPassword = async () => {
    const text = createdPassword || '';
    try {
      // navigator.clipboard only exists on HTTPS/localhost. The app runs on plain
      // HTTP, so fall back to a temp-textarea + execCommand copy there.
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (!ok) throw new Error('execCommand copy failed');
      }
      setMessage('Password copied to clipboard');
    } catch (e) {
      setMessage('Copy failed — select the password text and copy it manually');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm(`Generate a NEW password for "${userId}"? The old password will stop working.`)) return;
    setError('');
    setMessage('');
    try {
      const result = await api.adminResetPassword(userId);
      if (result.status === 'success') {
        setCreatedPassword(result.password);
        setEmailStatus(result.email_status || 'unknown');
        setMessage(`New password for "${userId}" — ${result.email_status === 'success' ? 'sent to their email' : 'share it manually'}`);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Failed to reset password');
    }
  };

  const openClient = (userId) => {
    setSelectedClient(userId);
    setClientDetail(null);
    loadClientDetail(userId);
  };

  // ── per-user segment permissions editor ──
  const ALL_SEGMENTS = [
    ['equity', 'Equity'], ['options', 'Options'], ['futures', 'Futures'],
    ['commodity', 'MCX Futures'], ['mcx_options', 'MCX Options'], ['sensex', 'Sensex'],
  ];
  const [segUser, setSegUser] = useState(null);       // user_id being edited
  const [segChecks, setSegChecks] = useState({});     // segment -> bool

  const openSegEditor = (u) => {
    const allowed = u.allowed_segments; // null/undefined = all allowed
    const checks = {};
    ALL_SEGMENTS.forEach(([k]) => { checks[k] = allowed == null ? true : allowed.includes(k); });
    setSegChecks(checks);
    setSegUser(u.user_id);
  };

  const saveSegEditor = async () => {
    const allOn = ALL_SEGMENTS.every(([k]) => segChecks[k]);
    // all boxes ticked -> store null (= all allowed, incl. future segments)
    const segments = allOn ? null : ALL_SEGMENTS.filter(([k]) => segChecks[k]).map(([k]) => k);
    try {
      const r = await api.adminSetUserSegments(segUser, segments);
      if (r.status === 'success') { setMessage(r.message); setSegUser(null); loadUsers(); }
      else setError(r.message);
    } catch (e) { setError('Failed to save segment permissions'); }
  };

  const recStatusLabel = {
    RECOMMENDED: 'Recommended',
    TRADED: 'In Trade',
    CLOSED: 'Closed',
    ORDER_FAILED: 'Order Failed',
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h2>Admin Panel</h2>
        <button className="admin-close-btn" onClick={onClose}>Back to Dashboard</button>
      </div>

      <div className="admin-tabs">
        <button className={`admin-tab ${activeTab === 'trading' ? 'active' : ''}`} onClick={() => setActiveTab('trading')}>
          Client Trading
        </button>
        <button className={`admin-tab ${activeTab === 'ai' ? 'active' : ''}`} onClick={() => setActiveTab('ai')}>
          AI Stock Picks
        </button>
        <button className={`admin-tab ${activeTab === 'performance' ? 'active' : ''}`} onClick={() => setActiveTab('performance')}>
          Real Performance
        </button>
        <button className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`} onClick={() => setActiveTab('users')}>
          User Management
        </button>
        <button className={`admin-tab ${activeTab === 'health' ? 'active' : ''}`} onClick={() => setActiveTab('health')}>
          System Health
        </button>
      </div>

      {message && <div className="admin-message success">{message}</div>}
      {error && <div className="admin-message error">{error}</div>}

      {/* ─────────── CLIENT TRADING TAB ─────────── */}
      {activeTab === 'trading' && (
        <div>
          {segHalt && (
            <div style={{ background: '#161d2b', border: '1px solid #263349', borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>🛑 Stop / Resume Trading by Segment <span style={{ fontWeight: 400, color: '#8aa0bd', fontSize: 12 }}>(applies to all users; open positions still exit)</span></div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {segHalt.segments.map((s) => {
                  const halted = segHalt.halted[s];
                  return (
                    <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#0f1523', border: '1px solid #263349', borderRadius: 8, padding: '6px 10px' }}>
                      <span style={{ textTransform: 'capitalize', minWidth: 92 }}>{s.replace('_', ' ')}</span>
                      <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 5, background: halted ? '#3a1414' : '#123a1e', color: halted ? '#f87171' : '#4ade80' }}>{halted ? 'STOPPED' : 'TRADING'}</span>
                      <button onClick={() => toggleSeg(s, !halted)} style={{ border: 0, borderRadius: 6, padding: '5px 12px', fontWeight: 700, cursor: 'pointer', color: '#fff', background: halted ? '#16a34a' : '#dc2626' }}>{halted ? 'RESUME' : 'STOP'}</button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {overview && (
            <div className="trading-summary-bar">
              <span className={`engine-badge ${overview.engine_running ? 'on' : 'off'}`}>
                {overview.engine_running ? '● Engine Running' : '○ Engine Stopped'}
              </span>
              <span className="mode-chip">{String(overview.mode || '').toUpperCase()}</span>
              <span className="mode-chip">{String(overview.segment || '').toUpperCase()}</span>
              <span className="summary-pnl">Day P&L: <PnL value={overview.daily_pnl} /></span>
              <span className="summary-pnl">Total P&L: <PnL value={overview.total_pnl} /></span>
            </div>
          )}

          <div className="admin-users-table">
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Mode</th>
                  <th>Segment</th>
                  <th>Login / Broker</th>
                  <th>Balance</th>
                  <th>Open Pos</th>
                  <th>Trades Today</th>
                  <th>W / L</th>
                  <th>Realized P&L</th>
                  <th>Unrealized</th>
                  <th>Total P&L</th>
                  <th>Last Trade</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(overview?.clients || []).map(c => (
                  <tr key={c.user_id} className={selectedClient === c.user_id ? 'row-selected' : ''}>

                    <td>
                      <span className="user-id">{c.user_id}</span>
                      {c.name && <span className="client-name"> {c.name}</span>}
                    </td>
                    <td>
                      <span className={`rec-status ${c.trade_mode === 'live' ? 'closed' : c.trade_mode === 'paper' ? 'traded' : 'order_failed'}`}>
                        {String(c.trade_mode || '').toUpperCase()}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.72rem' }}>
                      {(c.trade_segments && c.trade_segments.length)
                        ? c.trade_segments.map(s => (
                            <span key={s} style={{ display: 'inline-block', color: (s === 'commodity' || s === 'mcx_options') ? '#ffb020' : '#00d9ff', border: '1px solid #333', borderRadius: '4px', padding: '0 6px', marginRight: '3px', marginBottom: '2px' }}>
                              {String(s).replace('mcx_options', 'MCX-OPT').replace('commodity', 'MCX-FUT').toUpperCase()}
                            </span>))
                        : <span style={{ color: '#666' }}>—</span>}
                    </td>
                    <td style={{ fontSize: '0.72rem' }}>
                      {/* online = app open now; background = logged in but app
                          closed (trades still flow, user design); offline = logged out */}
                      <span style={{ color: c.app_logged_in ? '#00ff88' : (c.trading_available ? '#ffb020' : '#666') }}>
                        ● {c.app_logged_in ? 'online' : (c.trading_available ? 'background (trading on)' : 'offline')}
                      </span>
                      <br />
                      <span style={{ color: c.brokers_connected?.length ? '#00ff88' : '#ff4444' }}>
                        ⛁ {c.brokers_connected?.length ? c.brokers_connected.join(' + ') : 'no broker'}
                      </span>
                    </td>
                    <td>{fmtRs(c.balance)}</td>
                    <td>{c.open_positions}</td>
                    <td>{c.trades_today}</td>
                    <td><span className="pnl-pos">{c.wins}</span> / <span className="pnl-neg">{c.losses}</span></td>
                    <td><PnL value={c.realized_pnl} /></td>
                    <td><PnL value={c.unrealized_pnl} /></td>
                    <td><PnL value={c.total_pnl} /></td>
                    <td>{c.last_trade_time || '—'}</td>
                    <td>
                      <button className="admin-btn small" onClick={() => openClient(c.user_id)}>Details</button>
                    </td>
                  </tr>
                ))}
                {!overview?.clients?.length && (
                  <tr><td colSpan="13" className="empty-cell">No client trading data yet</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {selectedClient && (
            <div className="client-detail-panel">
              <div className="client-detail-header">
                <h3>{selectedClient} — Trading Detail</h3>
                <button className="admin-btn small" onClick={() => { setSelectedClient(null); setClientDetail(null); }}>Close</button>
              </div>

              <h4>Open Positions ({clientDetail?.positions?.length || 0})</h4>
              {closeMsg && <div style={{ color: '#00d9ff', fontSize: '0.78rem', marginBottom: '0.4rem' }}>{closeMsg}</div>}
              <div className="admin-users-table">
                <table>
                  <thead>
                    <tr>
                      <th>Symbol</th><th>Side</th><th>Qty</th><th>Entry</th><th>Current</th>
                      <th>Stop Loss</th><th>Target</th><th>Unrealized P&L</th><th>Since</th><th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(clientDetail?.positions || []).map((p, i) => (
                      <tr key={i}>
                        <td className="user-id">{p.trade_symbol || p.symbol}</td>
                        <td><span className={`side-badge ${p.side === 'SELL' ? 'sell' : 'buy'}`}>{p.side}</span></td>
                        <td>{p.quantity}</td>
                        <td>{fmtRs(p.entry_price)}</td>
                        <td>{fmtRs(p.current_price)}</td>
                        <td>{fmtRs(p.stop_loss)}</td>
                        <td>{fmtRs(p.take_profit)}</td>
                        <td><PnL value={p.unrealized_pnl} /></td>
                        <td>{p.entry_time}</td>
                        <td>
                          <button className="admin-btn small" style={{ background: '#3a1a1a', color: '#ff6666', border: '1px solid #662222' }}
                            disabled={closingPos === p.symbol}
                            onClick={async () => {
                              if (!window.confirm(`Close ${selectedClient}'s ${p.trade_symbol || p.symbol} position now?`)) return;
                              setClosingPos(p.symbol); setCloseMsg('');
                              const r = await api.closePosition(p.symbol).catch(() => ({ status: 'error', message: 'failed' }));
                              setCloseMsg(`${p.trade_symbol || p.symbol}: ${r.message || r.status}`);
                              setClosingPos(null);
                              loadClientDetail(selectedClient);
                            }}>{closingPos === p.symbol ? 'Closing…' : 'Close'}</button>
                        </td>
                      </tr>
                    ))}
                    {!clientDetail?.positions?.length && (
                      <tr><td colSpan="10" className="empty-cell">No open positions</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <h4>Today's Trades ({clientDetail?.trades?.length || 0})</h4>
              <div className="admin-users-table">
                <table>
                  <thead>
                    <tr>
                      <th>Time</th><th>Symbol</th><th>Action</th><th>Qty</th><th>Price</th>
                      <th>LTP Now</th><th>P&L</th><th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(clientDetail?.trades || []).slice().reverse().map((t, i) => (
                      <tr key={i}>
                        <td>{t.time}</td>
                        <td className="user-id">{t.symbol}</td>
                        <td><span className={`side-badge ${t.action === 'SELL' ? 'sell' : 'buy'}`}>{t.action}</span></td>
                        <td>{t.quantity}</td>
                        <td>{fmtRs(t.price)}</td>
                        <td>{t.ltp ? fmtRs(t.ltp) : '—'}</td>
                        <td>{t.pnl !== null && t.pnl !== undefined ? <PnL value={t.pnl} /> : <span className="pnl-flat">open</span>}</td>
                        <td className="reason-cell">{t.reason || (t.reasons || []).join(', ') || '—'}</td>
                      </tr>
                    ))}
                    {!clientDetail?.trades?.length && (
                      <tr><td colSpan="8" className="empty-cell">No trades today</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─────────── AI STOCK PICKS TAB ─────────── */}
      {activeTab === 'ai' && (
        <div>
          <p className="tab-hint">
            Every stock the AI selected as its best pick — why it was chosen, whether a trade was
            placed, and the full outcome. Click a row for the AI's reasoning.
          </p>
          <div className="admin-users-table">
            <table>
              <thead>
                <tr>
                  <th>Time</th><th>Symbol</th><th>Side</th><th>Conf</th><th>ML</th>
                  <th>Price @ Pick</th><th>Now</th><th>Move</th><th>Status</th>
                  <th>Entry</th><th>Exit</th><th>P&L</th><th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {recs.map((r) => (
                  <React.Fragment key={r.id + '-' + r.time}>
                    <tr className="rec-row" onClick={() => setExpandedRec(expandedRec === r.id ? null : r.id)}>
                      <td>{r.time}</td>
                      <td className="user-id">
                        {r.strike ? (
                          <>
                            <div style={{ fontWeight: 700 }}>{r.symbol} {r.strike} {r.option_type}</div>
                            <div style={{ fontSize: '0.62rem', color: '#00d9ff' }}>{r.contract}</div>
                            {r.oi && r.oi.buildup ? (
                              <div style={{ fontSize: '0.6rem', color: '#bbb' }}>
                                Fut OI {r.oi.fut_oi_chg_pct > 0 ? '+' : ''}{r.oi.fut_oi_chg_pct}% · {String(r.oi.buildup).replace('_', ' ')}
                                {r.oi.opt_oi_chg_pct !== undefined ? ` · opt OI ${r.oi.opt_oi_chg_pct > 0 ? '+' : ''}${r.oi.opt_oi_chg_pct}%` : ''}
                                {r.oi.fut_depth_imb !== undefined && r.oi.fut_depth_imb !== null ? ` · book ${r.oi.fut_depth_imb > 0 ? 'buyers' : 'sellers'} ${Math.abs(Math.round(r.oi.fut_depth_imb * 100))}%` : ''}
                              </div>
                            ) : null}
                            {r.bp && r.bp.tf ? (
                              <div style={{ fontSize: '0.6rem', color: r.bp.side === 'BUYING' ? '#ffd700' : r.bp.side === 'SELLING' ? '#c084fc' : '#888' }}
                                   title={r.bp.text || ''}>
                                Big player {r.bp.side}: {['5m', '15m', '1h', 'Day'].map(k => `${k} ${((r.bp.tf[k] || {}).side || '-').slice(0, 4)}`).join(' · ')}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <>
                            {r.symbol}
                            {r.segment ? <div style={{ fontSize: '0.62rem', color: '#888' }}>{r.segment}</div> : null}
                          </>
                        )}
                      </td>
                      <td><span className={`side-badge ${r.side === 'SELL' ? 'sell' : 'buy'}`}>{r.side}</span></td>
                      <td>{Math.round((r.confidence || 0) * 100)}%</td>
                      <td>{r.ml_proba > 0 ? `${Math.round(r.ml_proba * 100)}%` : '—'}</td>
                      <td>
                        {(r.strike && (r.price_is_premium || r.entry_price))
                          ? fmtRs(r.price_is_premium ? r.price_at_reco : r.entry_price)
                          : (r.strike ? '—' : fmtRs(r.price_at_reco))}
                        {r.strike && r.underlying_price ? (
                          <div style={{ fontSize: '0.62rem', color: '#888' }}>spot {fmtRs(r.underlying_price)}</div>
                        ) : null}
                      </td>
                      <td>{r.current_price ? fmtRs(r.current_price) : '—'}</td>
                      <td>{r.move_pct !== null && r.move_pct !== undefined
                        ? <span className={r.move_pct > 0 ? 'pnl-pos' : r.move_pct < 0 ? 'pnl-neg' : 'pnl-flat'}>{r.move_pct > 0 ? '+' : ''}{r.move_pct}%</span>
                        : '—'}</td>
                      <td><span className={`rec-status ${(r.status || '').toLowerCase()}`}>{recStatusLabel[r.status] || r.status}</span></td>
                      <td>{r.entry_price ? `${fmtRs(r.entry_price)} ×${r.quantity}` : '—'}</td>
                      <td>{r.exit_price ? fmtRs(r.exit_price) : '—'}</td>
                      <td>{r.status === 'CLOSED' ? <PnL value={r.pnl} pct={r.pnl_pct} /> : '—'}</td>
                      <td className="reason-cell">
                        {r.status === 'CLOSED' ? (r.exit_reason || '—')
                          : r.status === 'ORDER_FAILED' ? (r.note || 'order failed')
                          : r.status === 'TRADED' ? `holding since ${r.entry_time}`
                          : r.skip_reason ? <span title={r.skip_reason} style={{ color: '#ffb347' }}>not traded — {r.skip_reason}</span>
                          : 'not traded'}
                      </td>
                    </tr>
                    {expandedRec === r.id && (
                      <tr className="rec-detail-row">
                        <td colSpan="13">
                          <div className="rec-detail">
                            <strong>Why the AI picked {r.symbol}
                              {r.strike ? ` ${r.strike} ${r.option_type}` : ''}:</strong>
                            {r.contract && r.contract !== r.symbol && (
                              <div style={{ color: '#00d9ff', fontSize: '0.75rem', margin: '0.2rem 0' }}>
                                Contract: {r.contract} · picked at {r.time}
                                {r.entry_time ? ` · entered ${r.entry_time}` : ''}
                              </div>
                            )}
                            <ul>
                              {(r.reasons || []).map((reason, i) => <li key={i}>{reason}</li>)}
                              {!(r.reasons || []).length && <li>No detailed reasons recorded</li>}
                            </ul>
                            <div className="rec-detail-meta">
                              Signal score: {Math.round(r.score || 0)} · Quality: {Math.round(r.quality_score || 0)} ·
                              Picked from {r.qualified_count} qualified · Mode: {r.mode} · Segment: {r.segment}
                              {r.stop_loss ? ` · SL: ${fmtRs(r.stop_loss)} · Target: ${fmtRs(r.take_profit)}` : ''}
                              {r.trade_user ? ` · Account: ${r.trade_user}` : ''}
                            </div>
                            {(r.post_exit_move_pct !== undefined && r.post_exit_move_pct !== null) && (
                              <div className={`rec-post-exit ${r.post_exit_move_pct >= 5 ? 'regret' : ''}`}>
                                After exit: price moved {r.post_exit_move_pct > 0 ? '+' : ''}{r.post_exit_move_pct}% within 30 min
                                {r.post_15m ? ` (15m: ${fmtRs(r.post_15m)}` : ''}{r.post_30m ? `, 30m: ${fmtRs(r.post_30m)})` : r.post_15m ? ')' : ''}
                                {r.post_exit_move_pct >= 5 && ' — exited too early on this one'}
                                {r.post_exit_move_pct <= -5 && ' — good exit, dodged a bigger loss'}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {!recs.length && (
                  <tr><td colSpan="13" className="empty-cell">No AI picks yet today — they appear here as soon as the engine selects a stock</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─────────── REAL PERFORMANCE TAB ─────────── */}
      {activeTab === 'performance' && (
        <div className="perf-tab">
          {mlStatus && (() => {
            const sc = Math.max(0, Math.min(100, mlStatus.maturity_score || 0));
            const col = sc >= 60 ? '#16a34a' : sc >= 35 ? '#eab308' : '#f97316';
            return (
              <div style={{ padding: '0.9rem 1rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, marginBottom: '0.9rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '0.4rem' }}>
                  <span style={{ fontSize: '1rem', fontWeight: 800 }}>🧠 AI Maturity Level</span>
                  <span style={{ fontSize: '1.1rem', fontWeight: 800, color: col }}>{sc}% · {mlStatus.maturity_label}</span>
                </div>
                <div style={{ height: 12, background: 'rgba(255,255,255,0.1)', borderRadius: 6, marginTop: 8, overflow: 'hidden' }}>
                  <div style={{ width: `${sc}%`, height: '100%', background: col, transition: 'width 0.5s ease' }} />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.2rem', marginTop: 10, fontSize: '0.8rem', color: '#94a3b8' }}>
                  <span>Model edge (AUC): <b style={{ color: '#e2e8f0' }}>{mlStatus.test_auc ?? '—'}</b> <span style={{ color: '#64748b' }}>(0.50 = coin-flip)</span></span>
                  <span>Learned from: <b style={{ color: '#e2e8f0' }}>{mlStatus.trained_on || 0}</b> trades</span>
                  {mlStatus.recent_live_winrate != null && <span>Live win-rate: <b style={{ color: '#22c55e' }}>{mlStatus.recent_live_winrate}%</b></span>}
                  <span>Trusted: <b style={{ color: mlStatus.trusted ? '#22c55e' : '#f97316' }}>{mlStatus.trusted ? 'yes' : 'not yet'}</b></span>
                </div>
                {mlStatus.by_kind && (
                  // SEPARATE MODELS (owner 2026-11-03): index options and stock options each have their own model + AUC
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8, marginTop: 10 }}>
                    {[['📈 Index options', 'index'], ['🏢 Stock options', 'stock'], ['Combined (MCX + fallback)', 'all']].map(([t, k]) => {
                      const m = mlStatus.by_kind[k] || {};
                      const a = m.test_auc;
                      const ac = a == null ? '#64748b' : a >= 0.6 ? '#22c55e' : a > 0.55 ? '#eab308' : '#f97316';
                      return (
                        <div key={k} style={{ padding: '0.55rem 0.7rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: '0.78rem', color: '#94a3b8' }}>
                          <div style={{ fontWeight: 700, color: '#e2e8f0', marginBottom: 3 }}>{t}</div>
                          <div>AUC (held-out test): <b style={{ color: ac }}>{m.loaded ? a : 'no own model yet'}</b></div>
                          <div>AUC on real results: <b style={{ color: '#e2e8f0' }}>{m.live_auc ?? '—'}</b> ({m.live_n || 0} trades)</div>
                          <div>{m.loaded ? `${m.model_name || 'model'} · ${m.trained_on} examples${m.trained_at ? ` · ${m.trained_at}` : ''}` : 'first training tonight after market close'}</div>
                          {k !== 'all' && m.answers_with && m.answers_with !== k && <div style={{ color: '#64748b' }}>answering now with the combined model</div>}
                        </div>
                      );
                    })}
                  </div>
                )}
                <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 7 }}>
                  Continuous learning ON (day &amp; night){mlStatus.next_retrain_mins != null ? ` · next retrain in ~${mlStatus.next_retrain_mins} min` : ''}{mlStatus.last_retrain ? ` · last: ${mlStatus.last_retrain}` : ''}. Maturity climbs as it learns from more real trade outcomes.
                </div>
              </div>
            );
          })()}
          {!perf && <div style={{ padding: 20, opacity: 0.7 }}>Loading real performance…</div>}
          {perf && (
            <>
              <div style={{ padding: '10px 14px', marginBottom: 14, background: 'rgba(255,193,7,0.08)',
                            border: '1px solid rgba(255,193,7,0.4)', borderRadius: 8, fontSize: 13, lineHeight: 1.5 }}>
                <b>These are REAL closed-trade results — not a backtest, not a target.</b> {perf.note}
              </div>

              {perf.overall.trades === 0 ? (
                <div style={{ padding: 20, opacity: 0.7 }}>
                  No closed trades journaled yet. Figures appear here as the engine completes real/paper trades.
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
                    {[
                      { label: 'Closed Trades', val: perf.overall.trades },
                      { label: 'Win Rate', val: `${perf.overall.win_rate}%` },
                      { label: 'Expectancy / trade', val: `${perf.overall.expectancy_pct >= 0 ? '+' : ''}${perf.overall.expectancy_pct}%`,
                        good: perf.overall.expectancy_pct > 0 },
                      { label: 'Profit Factor', val: perf.overall.profit_factor, good: perf.overall.profit_factor >= 1 },
                      { label: 'Net P&L (₹)', val: perf.overall.net_pnl, good: perf.overall.net_pnl >= 0 },
                      { label: 'Avg Win / Avg Loss', val: `+${perf.overall.avg_win_pct}% / ${perf.overall.avg_loss_pct}%` },
                    ].map((c) => (
                      <div key={c.label} style={{ flex: '1 1 150px', minWidth: 140, padding: '12px 14px',
                            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }}>
                        <div style={{ fontSize: 11, opacity: 0.65, textTransform: 'uppercase', letterSpacing: 0.5 }}>{c.label}</div>
                        <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4,
                              color: c.good === undefined ? '#e6e6e6' : (c.good ? '#22c55e' : '#ef4444') }}>{c.val}</div>
                      </div>
                    ))}
                  </div>

                  <h4 style={{ margin: '18px 0 8px' }}>By Segment</h4>
                  <div className="admin-users-table">
                    <table>
                      <thead>
                        <tr><th>Segment</th><th>Trades</th><th>Win %</th><th>Expectancy</th><th>Profit Factor</th><th>Net ₹</th></tr>
                      </thead>
                      <tbody>
                        {Object.entries(perf.by_segment).map(([seg, s]) => (
                          <tr key={seg}>
                            <td style={{ textTransform: 'uppercase' }}>{seg}</td>
                            <td>{s.trades}</td>
                            <td>{s.win_rate}%</td>
                            <td style={{ color: s.expectancy_pct >= 0 ? '#22c55e' : '#ef4444' }}>
                              {s.expectancy_pct >= 0 ? '+' : ''}{s.expectancy_pct}%</td>
                            <td style={{ color: s.profit_factor >= 1 ? '#22c55e' : '#ef4444' }}>{s.profit_factor}</td>
                            <td style={{ color: s.net_pnl >= 0 ? '#22c55e' : '#ef4444' }}>{s.net_pnl}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <h4 style={{ margin: '18px 0 8px' }}>Where P&L comes from (exit reasons)</h4>
                  <div className="admin-users-table">
                    <table>
                      <thead><tr><th>Exit Reason</th><th>Count</th><th>Net ₹</th></tr></thead>
                      <tbody>
                        {perf.top_reasons.map((r) => (
                          <tr key={r.reason}>
                            <td>{r.reason}</td>
                            <td>{r.count}</td>
                            <td style={{ color: r.pnl >= 0 ? '#22c55e' : '#ef4444' }}>{r.pnl}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              <h4 style={{ margin: '18px 0 8px' }}>ML Model (self-improving nightly)</h4>
              <div style={{ padding: '12px 14px', background: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, fontSize: 13, lineHeight: 1.7 }}>
                {!perf.ml_model.loaded && <span>No ML model deployed yet — it trains automatically once enough real trades accumulate.</span>}
                {perf.ml_model.loaded && (
                  <>
                    <div>Held-out AUC: <b style={{ color: perf.ml_model.used_as_gate ? '#22c55e' : '#f59e0b' }}>
                      {perf.ml_model.test_auc}</b> (0.5 = coin flip; needs &gt; 0.55 to be trusted)</div>
                    <div>Test accuracy: <b>{(perf.ml_model.test_accuracy * 100).toFixed(1)}%</b></div>
                    <div>Trained on: <b>{perf.ml_model.trained_on}</b> examples</div>
                    <div>Currently used as a live trade gate: <b style={{ color: perf.ml_model.used_as_gate ? '#22c55e' : '#ef4444' }}>
                      {perf.ml_model.used_as_gate ? 'YES' : 'NO — still below the 0.55 trust floor'}</b></div>
                  </>
                )}
              </div>

              <h4 style={{ margin: '18px 0 8px' }}>Night Tasks (auto, after market close)</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ flex: '1 1 320px', padding: '12px 14px', background: 'rgba(255,255,255,0.04)',
                              border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, fontSize: 13, lineHeight: 1.7 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>🧠 Nightly ML Retrain</div>
                  {!perf.night_tasks?.last_retrain && <div style={{ opacity: 0.6 }}>Not run yet this session.</div>}
                  {perf.night_tasks?.last_retrain && (
                    <>
                      <div>Last run: <b>{perf.night_tasks.last_retrain.time}</b></div>
                      {perf.night_tasks.last_retrain.decision &&
                        <div style={{ color: perf.night_tasks.last_retrain.decision.includes('PROMOTED') ? '#22c55e' : '#f59e0b' }}>
                          {perf.night_tasks.last_retrain.decision}</div>}
                      {perf.night_tasks.last_retrain.summary &&
                        <div style={{ opacity: 0.8 }}>{perf.night_tasks.last_retrain.summary}</div>}
                    </>
                  )}
                </div>
                <div style={{ flex: '1 1 320px', padding: '12px 14px', background: 'rgba(255,255,255,0.04)',
                              border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, fontSize: 13, lineHeight: 1.7 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>📇 Daily Broker-Master Sync</div>
                  {!perf.night_tasks?.last_master_sync && <div style={{ opacity: 0.6 }}>Not run yet this session.</div>}
                  {perf.night_tasks?.last_master_sync && (
                    <>
                      <div>Last run: <b>{perf.night_tasks.last_master_sync.time}</b></div>
                      <div><b>{perf.night_tasks.last_master_sync.count}</b> underlyings verified
                        {perf.night_tasks.last_master_sync.new_count > 0 &&
                          <> · <b>{perf.night_tasks.last_master_sync.new_count}</b> new</>}</div>
                      {perf.night_tasks.last_master_sync.changes?.length > 0 && (
                        <div style={{ opacity: 0.85 }}>Lot changes: {perf.night_tasks.last_master_sync.changes.slice(0, 6).join(', ')}
                          {perf.night_tasks.last_master_sync.changes.length > 6 ? '…' : ''}</div>
                      )}
                    </>
                  )}
                </div>
                <div style={{ flex: '1 1 100%', padding: '12px 14px', background: 'rgba(255,255,255,0.04)',
                              border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, fontSize: 13, lineHeight: 1.7 }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>🛠️ Nightly Self-Diagnose &amp; Heal
                    {perf.night_tasks?.last_diagnostic && (
                      <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700,
                                     color: perf.night_tasks.last_diagnostic.healthy ? '#22c55e' : '#f59e0b' }}>
                        {perf.night_tasks.last_diagnostic.healthy ? 'ALL HEALTHY' : 'NEEDS ATTENTION'}</span>
                    )}
                  </div>
                  {!perf.night_tasks?.last_diagnostic && <div style={{ opacity: 0.6 }}>Runs nightly after 12 AM. Not run yet this session.</div>}
                  {perf.night_tasks?.last_diagnostic && (
                    <>
                      <div>Last run: <b>{perf.night_tasks.last_diagnostic.time}</b></div>
                      {perf.night_tasks.last_diagnostic.repairs?.length > 0 && (
                        <div style={{ color: '#22c55e' }}>✓ Auto-repaired: {perf.night_tasks.last_diagnostic.repairs.join(' · ')}</div>
                      )}
                      {perf.night_tasks.last_diagnostic.suggestions?.length > 0 ? (
                        <div style={{ color: '#f59e0b' }}>⚠ Suggestions: {perf.night_tasks.last_diagnostic.suggestions.join(' · ')}</div>
                      ) : (
                        <div style={{ color: '#22c55e' }}>No issues — system healthy for the day.</div>
                      )}
                      <div style={{ opacity: 0.6, fontSize: 11 }}>{(perf.night_tasks.last_diagnostic.checks || []).length} health checks passed</div>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ─────────── USER MANAGEMENT TAB ─────────── */}
      {activeTab === 'users' && (
        <div>
          {createdPassword && (
            <div className="created-password-box">
              <h4>Generated Password</h4>
              <div className="password-display">
                <code>{createdPassword}</code>
                <button className="admin-btn small" onClick={copyPassword}>Copy</button>
              </div>
              <p className="password-note">
                Password has been sent to user's email.
                {emailStatus === 'skipped' && ' (Email not configured — share password manually)'}
                {emailStatus === 'error' && ' (Email failed — share password manually)'}
              </p>
            </div>
          )}

          <div className="admin-actions">
            <button className="admin-btn primary" onClick={() => { setShowCreate(!showCreate); setCreatedPassword(''); }}>
              {showCreate ? 'Cancel' : '+ Create User'}
            </button>
          </div>

          {showCreate && (
            <div className="admin-create-form">
              <h3>Create New User</h3>
              <form onSubmit={handleCreate}>
                <div className="form-row">
                  <div className="form-group">
                    <label>User ID *</label>
                    <input type="text" placeholder="e.g. trader1" value={newUser.user_id}
                      onChange={e => setNewUser({ ...newUser, user_id: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Name *</label>
                    <input type="text" placeholder="Full name" value={newUser.name}
                      onChange={e => setNewUser({ ...newUser, name: e.target.value })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email * (password will be sent here)</label>
                    <input type="email" placeholder="user@email.com" value={newUser.email}
                      onChange={e => setNewUser({ ...newUser, email: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Role</label>
                    <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}>
                      <option value="user">User (Trade only)</option>
                      <option value="admin">Admin (Full access)</option>
                    </select>
                  </div>
                </div>
                <button type="submit" className="admin-btn primary" disabled={loading}>
                  {loading ? 'Creating...' : 'Create User & Send Password'}
                </button>
              </form>
            </div>
          )}

          <div className="admin-users-table">
            <table>
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <React.Fragment key={u.user_id}>
                  <tr>
                    <td className="user-id">{u.user_id}</td>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td>
                      <span className={`role-badge ${u.role}`}>{u.role}</span>
                    </td>
                    <td>
                      <span className={`status-badge ${u.active ? 'active' : 'inactive'}`}>
                        {u.active ? 'Active' : 'Inactive'}
                      </span>
                      {Array.isArray(u.allowed_segments) && (
                        <div style={{ fontSize: '0.62rem', color: '#ffb020', marginTop: '0.15rem' }}>
                          segments limited ({u.allowed_segments.length}/6)
                        </div>
                      )}
                    </td>
                    <td>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                    <td>
                      <button className="admin-btn small" onClick={() => handleResetPassword(u.user_id)}>
                        Reset Password
                      </button>
                      {u.user_id !== 'admin' && (
                        <>
                          <button className="admin-btn small" onClick={() => segUser === u.user_id ? setSegUser(null) : openSegEditor(u)}>
                            Segments
                          </button>
                          <button className="admin-btn small" onClick={() => handleToggle(u.user_id)}>
                            {u.active ? 'Disable' : 'Enable'}
                          </button>
                          <button className="admin-btn small danger" onClick={() => handleDelete(u.user_id)}>
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                  {segUser === u.user_id && (
                    <tr>
                      <td colSpan="7" style={{ background: '#12122a' }}>
                        <div style={{ padding: '0.5rem 0.3rem' }}>
                          <strong style={{ color: '#00d9ff', fontSize: '0.8rem' }}>
                            Allowed segments for {u.user_id}
                          </strong>
                          <span style={{ color: '#888', fontSize: '0.72rem', marginLeft: '0.6rem' }}>
                            unticked = blocked: hidden on their dashboard and never mirrored into their account
                          </span>
                          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', margin: '0.5rem 0' }}>
                            {ALL_SEGMENTS.map(([k, label]) => (
                              <label key={k} style={{ color: segChecks[k] ? '#00ff88' : '#ff4444', fontSize: '0.8rem', cursor: 'pointer' }}>
                                <input type="checkbox" checked={!!segChecks[k]}
                                  onChange={e => setSegChecks(p => ({ ...p, [k]: e.target.checked }))}
                                  style={{ marginRight: '0.3rem' }} />
                                {label}
                              </label>
                            ))}
                          </div>
                          <button className="admin-btn primary small" onClick={saveSegEditor}>Save Permissions</button>
                          <button className="admin-btn small" style={{ marginLeft: '0.4rem' }} onClick={() => setSegUser(null)}>Cancel</button>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'health' && health && (
        <div style={{ fontSize: 13 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            {[['Engine', health.health.engine_running, health.health.engine_running ? 'Running' : 'Stopped'],
              ['Broker (Fyers)', health.health.broker_connected, health.health.broker_connected ? 'Connected' : 'Disconnected']].map(([label, ok, txt]) => (
              <div key={label} style={{ background: '#161d2b', border: '1px solid #263349', borderRadius: 8, padding: '8px 12px' }}>
                <span style={{ color: '#8aa0bd' }}>{label}: </span>
                <span style={{ color: ok ? '#4ade80' : '#f87171', fontWeight: 700 }}>{txt}</span>
              </div>
            ))}
            <div style={{ background: '#161d2b', border: '1px solid #263349', borderRadius: 8, padding: '8px 12px' }}>
              <span style={{ color: '#8aa0bd' }}>Scan: </span>#{health.health.scan_count} @ {health.health.last_scan_time}
            </div>
            <div style={{ background: '#161d2b', border: '1px solid #263349', borderRadius: 8, padding: '8px 12px' }}>
              <span style={{ color: '#8aa0bd' }}>Segment: </span>{(health.health.segment || '').toUpperCase()}/{(health.health.exchange || '').toUpperCase()}
            </div>
            <div style={{ background: '#161d2b', border: '1px solid #263349', borderRadius: 8, padding: '8px 12px' }}>
              <span style={{ color: '#8aa0bd' }}>Open positions: </span>{health.health.open_positions}
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Halts</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {health.health.halt_new_entries && <span style={{ background: '#3a1414', color: '#f87171', padding: '3px 8px', borderRadius: 6 }}>ALL NEW ENTRIES HALTED</span>}
              {Object.entries(health.segment_halts).filter(([, v]) => v).map(([s]) => (
                <span key={s} style={{ background: '#3a1414', color: '#f87171', padding: '3px 8px', borderRadius: 6, textTransform: 'capitalize' }}>{s.replace('_', ' ')} STOPPED</span>
              ))}
              {!health.health.halt_new_entries && !Object.values(health.segment_halts).some(Boolean) && <span style={{ color: '#4ade80' }}>None — all segments trading</span>}
            </div>
            {Object.keys(health.user_locks || {}).length > 0 && (
              <div style={{ marginTop: 6, color: '#fbbf24' }}>Daily-locked users: {Object.entries(health.user_locks).map(([u, g]) => `${u} (Rs.${g.pnl})`).join(', ')}</div>
            )}
          </div>

          {health.diagnostic && (
            <div style={{ marginBottom: 12, background: '#161d2b', border: '1px solid #263349', borderRadius: 8, padding: '8px 12px' }}>
              <span style={{ fontWeight: 700 }}>Self-Diagnose: </span>
              <span style={{ color: health.diagnostic.healthy ? '#4ade80' : '#f87171' }}>{health.diagnostic.healthy ? 'HEALTHY' : 'ISSUES'}</span>
              <span style={{ color: '#8aa0bd' }}> @ {health.diagnostic.time}</span>
              {(health.diagnostic.suggestions || []).length > 0 && <div style={{ color: '#fbbf24', marginTop: 4 }}>{(health.diagnostic.suggestions || []).join(' · ')}</div>}
            </div>
          )}

          <div style={{ fontWeight: 700, marginBottom: 6 }}>Activity Log <span style={{ fontWeight: 400, color: '#8aa0bd', fontSize: 12 }}>(newest first, auto-refresh)</span></div>
          <div style={{ background: '#0b1120', border: '1px solid #263349', borderRadius: 8, padding: '8px 10px', maxHeight: 360, overflowY: 'auto', fontFamily: 'monospace', fontSize: 12 }}>
            {[...health.log].reverse().map((x, i) => {
              const c = x.category === 'error' ? '#f87171' : x.category === 'warn' ? '#fbbf24' : x.category === 'trade' ? '#4ade80' : x.category === 'system' ? '#60a5fa' : '#9fb3c8';
              return <div key={i} style={{ color: c, padding: '1px 0' }}><span style={{ color: '#5a6b82' }}>{x.time} </span>{x.message}</div>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;
